import { isOdspResolvedUrl } from '@fluidx/utilities';
import { configureIrmCapabilities } from './utilities/configureIrmCapabilities';
import { Scenario } from './telemetry/Scenario';
import { ErrorEvent } from './telemetry/ErrorEvent';
import { applyWatermark } from './utilities/applyWatermark';
import { configureMandatoryLabeling } from './utilities/configureMandatoryLabeling';
import { configureProtectedFile } from './utilities/configureProtectedFile';
import { ActivityTracker, NamespaceLoggerAdapter, sendTelemetryEvent } from '@fluidx/telemetry';
import { isMIPLocalCacheEnabled, isSetLabelEnabled } from './utilities/informationProtectionSettingsUtilities';
import { getInformationProtectionFromCache, updateInformationProtectionInCache } from './utilities/InformationProtectionCacheUtilities';
/**
 * Helper method which enforces all the information protection related policies.
 *  - IRM Capabilities: This function retrieves the current IRM capabilities and
 *    enforces the following
 *      * Disables print/cut/copy/paste operations if canExtract is set to false
 *      * Forces the container to read-only mode if canWrite is set to false
 *  - Render Watermark : This function retrieves the current watermark information
 *    and renders the watermark information in the provided Div element
 *  - Mandatory Labeling: If the policy requires mandatory labeling then the container
 *    is forced to read-only mode until a label is applied by the user.
 *  - Remove Fluid Cache: If the file is protected then this function removes the
 *    Fluid cache based on partitionKey.
 * @param container The container
 * @param resolvedUrl
 * @param componentDiv The div that loads the components
 * @param informationProtectionServicePromise The information protection service promise
 * @param partitionKey A string to specify what partition of the cache you wish to use (e.g. a user id).
 * Null can be used to explicity indicate no partitioning. A null value should only be used when the host
 * can ensure that the cache is not able to be shared with multiple users.
 * @param logger Optional logger for telemetry
 * @param settingsProvider A settings provider that returns flight info about enabled features
 */
export async function enforceInformationProtection(container, resolvedUrl, componentDiv, informationProtectionServicePromise, partitionKey, logger, settingsProvider) {
    if (!isOdspResolvedUrl(resolvedUrl)) {
        logger &&
            sendTelemetryEvent(logger, {
                eventName: ErrorEvent.InvalidResolvedUrl,
                entrypoint: Scenario.EnforceInformationProtection
            });
        return;
    }
    const mipLogger = logger ? new NamespaceLoggerAdapter('InformationProtectionService', logger) : undefined;
    const tracker = ActivityTracker.start('enforceInformationProtection', mipLogger, {
        addUserTiming: true,
        isAPerformanceMetric: true
    });
    let mipCache = undefined;
    if (isMIPLocalCacheEnabled(settingsProvider)) {
        // Try to retrieve the MIP data from browser local storage
        mipCache = getInformationProtectionFromCache(resolvedUrl, mipLogger);
    }
    try {
        const informationProtectionService = await informationProtectionServicePromise;
        container.on('closed', () => informationProtectionService.dispose?.());
        const componentDivSync = 'then' in componentDiv ? await componentDiv : componentDiv;
        // Kick off snapshot cache removal if the file is protected
        configureProtectedFile(informationProtectionService, resolvedUrl, partitionKey, mipCache, tracker);
        // Kick off watermark information rendering
        applyWatermark(componentDivSync, informationProtectionService, resolvedUrl, mipCache, tracker);
        await Promise.all([
            // Ensure IRM capabilities are enforced
            configureIrmCapabilities(componentDivSync, informationProtectionService, resolvedUrl, mipCache, tracker),
            // Ensure the container is in read only mode if mandatory label is required
            isSetLabelEnabled(settingsProvider) &&
                configureMandatoryLabeling(container, informationProtectionService, resolvedUrl, mipCache, tracker, mipLogger)
        ]);
        if (isMIPLocalCacheEnabled(settingsProvider)) {
            // Update the browser local storage with MIP data
            updateInformationProtectionInCache(resolvedUrl, informationProtectionService, mipLogger).catch(() => { });
        }
        tracker.setResult(true /*succeeded*/, {
            isCached: !!mipCache,
            currentLabel: !!mipCache?.currentLabel,
            policySettings: !!mipCache?.policySettings,
            watermark: !!mipCache?.watermark,
            hasProtection: !!mipCache?.hasProtection
        });
    }
    catch (error) {
        tracker.setResult(false /*succeeded*/, { errorType: ErrorEvent.EnforceInformationProtectionError }, error);
    }
}
//# sourceMappingURL=enforceInformationProtection.js.map